import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { TextField } from '@loadsmart/loadsmart-ui'
import { Button, Drawer, Field, Icon, Layout, Text } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { usePasteFromClipboard } from 'core/ui/hooks/usePasteFromClipboard'

import openDockAppointmentImg from '../assets/opendock-appointment.jpg'
import { useShipmentAnalytics } from '../hooks/useShipmentAnalytics'
import { useUpdateOpenDockAppointment } from '../hooks/useUpdateOpenDockAppointment'

export function AlreadyScheduled({
  setStep,
  previousStep,
  shipmentUUID,
  stopIndex,
  onConfirm,
  onClose,
}: {
  setStep: (step: string) => void
  previousStep: string | undefined
  shipmentUUID: string
  stopIndex: number
  onConfirm: () => void
  onClose: () => void
}) {
  const { track } = useShipmentAnalytics()

  const [code, setCode] = useState<string>('')

  const isDisabled = code.trim().length === 0

  const { paste } = usePasteFromClipboard({
    onSuccess: pastedText => {
      setCode(pastedText ?? '')
      toast.success('Confirmation number pasted!', {
        position: 'top-left',
      })
    },
  })

  const handlePasteText = useCallback(async () => {
    await paste()
  }, [paste])

  const { update: updateAppointment, requestStatus } = useUpdateOpenDockAppointment()

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      toast.success('Scheduled successfully.')
      onConfirm()
    }

    if (requestStatus === RequestStatus.Error) {
      toast.error('Couldn’t schedule. Try again.')
      onClose()
    }
  }, [onClose, requestStatus, onConfirm])

  return (
    <>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4">
          <Field required style={{ width: '100%' }}>
            <Field.Label>Confirmation number</Field.Label>
            <Layout.Group gap="spacing-2" align="center">
              <TextField
                name="code"
                value={code}
                onChange={event => setCode(event.target.value)}
                style={{ width: '240px' }}
              />
              <Button variant="tertiary" size="small" onClick={handlePasteText}>
                Paste code
              </Button>
            </Layout.Group>
          </Field>
          <Text variant="body-sm" color="color-text-tertiary">
            Copy the CONFIRMATION NUMBER received from Opendock after scheduling an appointment and
            paste it in the field above.
          </Text>
          <img
            src={openDockAppointmentImg}
            alt="opendock appointment hint"
            style={{ maxWidth: '285px' }}
          />
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary
          onClick={() => setStep(previousStep ?? 'instructions')}
          leading={<Icon name="arrow-left" color="color-text-primary" />}
        >
          Back
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary
          disabled={isDisabled}
          trailing={<Icon name="arrow-right" color="color-text-inverted" />}
          loading={requestStatus === RequestStatus.Pending}
          onClick={() => {
            track('Shipment details / Appointment scheduled', {
              hasId: 'YES',
              is_opendock: true,
            })
            updateAppointment({
              shipmentUUID,
              stopIndex,
              data: { confirmationNumber: code },
            })
          }}
        >
          Confirm
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
